'use client';

import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { API_URL } from '@eliadmin/core/src/api/fetcher';
import { fetchRelatedProducts } from '@eliadmin/core/src/api/products/related-products-api';
import {
  relatedProductsQueryKey,
  relatedProductsSession,
} from '@eliadmin/core/src/api/products/related-products-query';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchBestsellers } from '@eliadmin/core/src/components/nt/home/bestsellers-source';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import { Carousel } from '@eliadmin/core/src/components/nt/shared/carousel';
import { CarouselItem } from '@eliadmin/core/src/components/nt/shared/carousel-item';
import { env } from '@eliadmin/core/src/env';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

const HEADING_ID = 'mohlo-by-vas-zaujimat';

/** Related lists read for the first products of the cart (one request each, cached per product). */
const SOURCE_PRODUCTS = 3;
/** Fewer related products than one carousel view → top up with the bestsellers. */
const MIN_PRODUCTS = 4;
const MAX_PRODUCTS = 8;

/** Same columns as the PDP related products and the homepage bestsellers. */
const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1024px) calc((100vw - 136px) / 4), (min-width: 768px) calc((100vw - 96px) / 3), 260px';

/**
 * Figma „Section · Často kupujú spolu“ (166:12679): top border, padding 80 / 200, H2 32 px with
 * the prev/next Outline buttons, four `ProductTile`s per view from lg (3 from md, 260 px scrolling
 * below). Products already in the cart are left out; the section waits for the first related
 * lists, so the tiles do not swap after they appear, and is not rendered when nothing is left.
 */
export function CartRelatedCarousel({
  title,
  fallback,
  locale,
}: {
  title: string;
  /** Public bestsellers from the server. */
  fallback: ProductDisplayData[];
  locale: string;
}) {
  const { data: cart } = useCart();
  const me = useCustomerStore(state => state.me);
  const currency = useGlobalStore(state => state.generalLayout?.currency);
  const items = cart.items ?? [];
  const slugs = [...new Set(items.map(item => item.slug))].slice(0, SOURCE_PRODUCTS);
  const session = relatedProductsSession(me);

  const related = useQueries({
    queries: slugs.map(slug => ({
      queryKey: relatedProductsQueryKey({
        api: API_URL,
        store: env.NEXT_PUBLIC_CURRENT_STORE,
        locale,
        slug,
        currency,
        session,
        fields: {},
      }),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchRelatedProducts({ locale, slug, authToken: me?.authToken, signal }),
      staleTime: 60_000,
    })),
  });
  // A signed-in customer gets the bestsellers with the customer prices (homepage query).
  const { data: customerBestsellers } = useQuery({
    queryKey: ['nt-bestsellers', locale, me?.authToken],
    queryFn: () => fetchBestsellers({ locale, authToken: me?.authToken }),
    enabled: !!me?.authToken,
    staleTime: 5 * 60 * 1000,
  });

  // First paint waits for every related list; later cart changes keep the section in place.
  const [ready, setReady] = useState(false);
  const pending = related.some(query => query.isPending);
  if (!ready && !pending) setReady(true);
  if (!ready && pending) return null;

  const inCart = new Set(items.map(item => String(item.product.id)));
  const products: ProductDisplayData[] = [];
  const add = (list: ProductDisplayData[]) => {
    for (const product of list) {
      const id = String(product.id);
      if (inCart.has(id) || products.some(entry => String(entry.id) === id)) continue;
      products.push(product);
    }
  };
  for (const query of related) add(query.data?.data ?? []);
  if (products.length < MIN_PRODUCTS)
    add(customerBestsellers?.length ? customerBestsellers : fallback);
  if (!products.length) return null;

  return (
    <section
      aria-labelledby={HEADING_ID}
      className="custom-section border-t border-[#E2E5DF] py-14 md:py-20"
    >
      <div className="custom-container">
        <Carousel
          header={
            <div className="flex md:min-h-12 md:items-center">
              <h2 id={HEADING_ID} className="nt-h3 text-[#0D110D]">
                {title}
              </h2>
            </div>
          }
          className="flex flex-col gap-8"
          trackClassName="-mx-4 -my-1 gap-4 px-4 py-1 scroll-px-4 sm:-mx-6 sm:px-6 sm:scroll-px-6 md:-mx-1 md:gap-6 md:px-1 md:scroll-px-1"
        >
          {products.slice(0, MAX_PRODUCTS).map(product => (
            <CarouselItem
              key={product.id}
              className="w-[260px] md:w-[calc((100%-48px)/3)] lg:w-[calc((100%-72px)/4)]"
            >
              <ProductTile product={product} imageSizes={TILE_SIZES} />
            </CarouselItem>
          ))}
        </Carousel>
      </div>
    </section>
  );
}
